import {getRequest} from '@/Services/ApiService';
import APIURL from '@/api-url';
import DateFormat from '@/components/Common/dateFormat';
import Container from '@/components/atoms/Container';
import Icon from '@/components/atoms/Icon';
import SectionHeader from '@/components/molecules/SectionHeader';
import type {LatestInsightPost} from '@/types/listing';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import {memo, useEffect, useState} from 'react';
import parse from 'html-react-parser';
import {formatListingDate} from '@/util/blogDates';
import {LATEST_INSIGHTS_FIELDS} from '@/Services/latestInsights';
import TitleTooltip from './TitleTooltip';
import styles from './LatestInsights.module.scss';
const OptimizedImage = dynamic(() => import('@/components/atoms/OptimizedImage'));

interface LatestInsightsProps {
    id?: string;
    /**
     * Server-rendered posts. When provided (via getStaticProps), the cards are in
     * the initial HTML and the client fetch is skipped. When omitted, the
     * component falls back to fetching on mount (legacy, for un-migrated pages).
     */
    posts?: LatestInsightPost[];
    /** Reserve the 1200×628 image box before the lazy image loads (avoids layout shift). Homepage only for now. */
    reserveImageRatio?: boolean;
}

const LatestInsights = ({id, posts, reserveImageRatio = false}: LatestInsightsProps) => {
    const [blogData, setBlogData] = useState<LatestInsightPost[]>(posts ?? []);
    useEffect(() => {
        // SSR-provided posts are already in the HTML — don't re-fetch on the client.
        if (posts !== undefined) return undefined;
        const controller = new AbortController();
        // Only the fields the card renders — the full post payload is ~98 KB for three posts.
        const url = `${id ? `categories=${id}&per_page=3` : '&per_page=3'}&_fields=${LATEST_INSIGHTS_FIELDS}`;
        getRequest<LatestInsightPost[]>(`${APIURL.BLOGS_LISTING}?${url}`, controller.signal)
            .then((res) => {
                setBlogData(Array.isArray(res) ? res : []);
            })
            .catch(() => {
                setBlogData([]);
            });
        return () => controller.abort();
    }, [id, posts]);

    return (
        <div className="padding-bottom-zero">
            <Container>
                <SectionHeader layout="wide" headingSize="large" heading="Our Latest Insights" />
                <div className={styles.grid}>
                    {blogData?.map((blogItem) => {
                        return (
                            <div className={styles.item} key={blogItem.id}>
                                <div className="insights_card">
                                    <Link
                                        href={`blog/${blogItem?.slug}`}
                                        target=""
                                        className="menu-links"
                                        aria-label={`Read blog: ${blogItem?.title}`}
                                    >
                                        <OptimizedImage
                                            src={blogItem?.blog_image as string}
                                            alt={blogItem?.title as string}
                                            height={218}
                                            width={300}
                                            className={reserveImageRatio ? `insights_img ${styles.image}` : 'insights_img'}
                                            priority={false}
                                        />
                                    </Link>
                                    <div className="content">
                                        <div className="date_icon">
                                            <Icon name="CalendarMonth" />
                                            <p className={`insights_date font-regular-text ${styles.body1}`}>
                                                {formatListingDate(blogItem, DateFormat)}
                                            </p>
                                        </div>
                                        <div className={styles.titleContainer}>
                                            <TitleTooltip title={`${blogItem?.title}`}>
                                                <Link href={`blog/${blogItem?.slug}`} target="" className="card-redirection-heading">
                                                    <p className={`insights_card_heading font-headers ${styles.body1}`}>
                                                        {blogItem?.title}
                                                    </p>
                                                </Link>
                                            </TitleTooltip>
                                        </div>
                                        <div className={`insights_card_content font-regular-text ${styles.body1} ${styles.cardContent}`}>
                                            {blogItem.excerpt?.rendered ? parse(blogItem.excerpt.rendered) : ''}
                                        </div>
                                        <div className="spectrum-btn">
                                            <Link href={`blog/${blogItem?.slug}`} target="" className="menu-links">
                                                <button
                                                    type="button"
                                                    className={`${styles.button} insights_btn font-regular-text`}
                                                    aria-label={`Read article: ${blogItem?.title}`}
                                                >
                                                    Read Article
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={styles.footer}>
                    <Link href="/blog" className="menu-links">
                        <button
                            type="button"
                            className={`${styles.button} animated-button button-bg-primary font-regular-text`}
                            aria-label="Book Consultation"
                        >
                            View All Blogs
                        </button>
                    </Link>
                </div>
            </Container>
        </div>
    );
};
export default memo(LatestInsights);
